import { Injectable, inject } from "@angular/core";
import { ClToken2Service } from "@carnegie-learning/data-access-angular-fido";
import {
    Environment,
    LtiResourceBuilder,
    Resource,
} from "@carnegie-learning/feature-lti";
import { Asset, assetTypeIds } from "@carnegie-learning/cure-types";

import { environment as appEnvironment } from "../../../environments/environment";

interface LtiLaunchSuccess {
    success: true;
}

interface LtiLaunchFailure {
    success: false;
    error: unknown;
}

export type LtiLaunchResponse = LtiLaunchSuccess | LtiLaunchFailure;

/**
 * Drives the browser-side half of an LTI 1.3 Deep Linking response handoff.
 * The teacher (in their LMS-launched session) selected content in Passport;
 * Passport already created the internal assignment; this service asks
 * lti-receiver to mint a signed DL response JWT for those content_items
 * and replays the resulting auto-submit form in the teacher's browser, so
 * the LMS receives the signed JWT at its deep_link_return_url and creates
 * the corresponding LMS-side assignment.
 *
 * Per the LTI 1.3 DL spec, response delivery MUST go through the user
 * agent — server-to-server replay is not permitted. The pattern below
 * (POST → 303 with auto-submit HTML → blob URL navigation) mirrors
 * tutorstream's LtiResourceService:
 * https://github.dev.carnegielearning.com/CarnegieLearning/tutorstream/blob/main/packages/feature/asset-actions/src/lib/asset-actions/lti-resource.service.ts
 *
 * Content_items are built via the shared `LtiResourceBuilder` from
 * `@carnegie-learning/feature-lti` so resource URLs and lineItem rules
 * stay consistent with the other tools that call into this same flow.
 */
@Injectable({ providedIn: "root" })
export class LtiReceiverService {
    private clToken2Service = inject(ClToken2Service);

    async submitDeepLinkResponse(
        launchId: string,
        assets: Asset[]
    ): Promise<LtiLaunchResponse> {
        try {
            const baseUrl = appEnvironment.LTI_RECEIVER_URI;
            if (!baseUrl) {
                return {
                    success: false,
                    error: "LTI_RECEIVER_URI is not configured",
                };
            }

            const env = this.getEnvironment();
            const contentItems: Resource[] = assets.map((asset) =>{
                //hack to make this work until CURE-2234 is done
                //the asset type id is only use to check if it's "pickable" and throw if it isn't
                const hackedAsset:Asset = {
                    ...asset,
                    assetTypeId: assetTypeIds.SEC_LINK
                };
                return new LtiResourceBuilder(env, hackedAsset).getResource()
            });

            const url = `${baseUrl}/dlservice/json/${encodeURIComponent(launchId)}`;
            const response = await fetch(url, {
                method: "POST",
                headers: new Headers({
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${this.clToken2Service.getClToken2()}`,
                }),
                body: JSON.stringify(contentItems),
            });

            // lti-receiver replies with 303 + an auto-submit HTML form whose
            // action is the LMS's deep_link_return_url. Navigate the browser
            // to a blob URL containing that HTML so the form's body-onload
            // handler fires and POSTs the signed JWT to the LMS.
            if (response.status === 303 || response.ok) {
                await this.replayAutoSubmitForm(response);
                return { success: true };
            }
            return { success: false, error: `lti-receiver returned ${response.status}` };
        } catch (e) {
            return { success: false, error: e };
        }
    }

    private getEnvironment(): Environment {
        // passport-frontend-v2 doesn't currently distinguish `staging` from
        // `production`, and dev/local/aqa all run against qa backends.
        // Mirror tutorstream's mapping until we have a finer signal.
        return appEnvironment.production ? Environment.production : Environment.qa;
    }

    private async replayAutoSubmitForm(response: Response): Promise<void> {
        const html = await response.text();
        const blob = new Blob([html], { type: "text/html" });
        const blobUrl = URL.createObjectURL(blob);
        window.location.href = blobUrl;
    }
}
